import { ChangeEvent, useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AuthenticationContext } from "../../components/authentication/AuthenticationContext";
import { deleteArtistLogo, getArtist, getArtistLogo, postArtist, postArtistLogo } from "../../components/apis/music-api";
import { Box, Button, Divider, Stack, TextField, Typography, useMediaQuery } from "@mui/material";
import React from "react";
import ButtonBar from "../../components/button-bar/ButtonBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCloudArrowUp, faFloppyDisk, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { Artist, Location, Style, TimePeriod } from "../../types/Artist";
import StyleRow from "./StyleRow";
import { FileResponse } from "../../types/FileResponse";
import { BeatLoader } from "react-spinners";
import callApi from "../../components/call-api/call-api";
import PeriodRow from "./PeriodRow";
import DateField from "./DateField";

type EditArtistProps = {
    letter: string,
    artistName: string,
}

const EditArtist = () => {
    const params = useParams<EditArtistProps>();
    const navigate = useNavigate();
    const { getAccessToken } = useContext(AuthenticationContext);
    const [ artist, setArtist ] = useState<Artist>();
    const [ newStyleKey, setNewStyleKey ] = useState('');
    const [ newStyleValue, setNewStyleValue ] = useState('');
    const [ newPeriodOriginDate, setNewPeriodOriginDate ] = useState(-1);
    const [ newPeriodOriginMonth, setNewPeriodOriginMonth ] = useState(-1);
    const [ newPeriodOriginYear, setNewPeriodOriginYear ] = useState(-1);
    const [ newPeriodEndDate, setNewPeriodEndDate ] = useState(-1);
    const [ newPeriodEndMonth, setNewPeriodEndMonth ] = useState(-1);
    const [ newPeriodEndYear, setNewPeriodEndYear ] = useState(-1);
    const [ logo, setLogo ] = useState<FileResponse>();
    const [ selectedFile, setSelectedFile ] = useState<File | null>(null);
    const isSmall = useMediaQuery('(max-width:1000px)');
    
    useEffect(() => {
        callApi<Artist>(getArtist(getAccessToken(), params.artistName ?? ''), setArtist);
        callApi<FileResponse>(getArtistLogo(getAccessToken(), params.artistName ?? ''), setLogo);
    }, [params, getAccessToken]);
        
    const save = async () => {
        if (artist !== undefined) {
            await postArtist(getAccessToken(), params.artistName ?? '', artist);
            navigate(`/letters/${params.letter}/artists/${artist.name}`);
        }
    }
    
    const handleSave = () => {
        if (params.letter !== undefined && params.artistName !== undefined) {
            save();
        }
    }
    
    const handleStyleDelete = (index: number) => {
        if (artist !== undefined) {
            const s: Style[] = artist.styles === undefined ? [] : [ ...artist.styles ];
            s.splice(index, 1);
            setArtist({ ...artist, styles: s });
        }
    }
    
    const handleStyleChange = (index: number, style: Style) => {
        if (artist !== undefined) {
            const styles = artist.styles;
            if (styles !== undefined) {
                const s: Style[] =[
                    ...styles.slice(0, index),
                    style,
                    ...styles.slice(index + 1),
                ];

                setArtist({ ...artist, styles: s });
            }
        }
    }
    
    const handleAddStyle = () => {
        if (artist !== undefined) {
            const styles: Style[] = artist.styles === undefined ? [] : [ ...artist.styles ];
            styles.push({ key: newStyleKey, value: newStyleValue });
            setArtist({ ...artist, styles });
            setNewStyleKey('');
            setNewStyleValue('');
        }
    }
    
    const handlePeriodDelete = (index: number) => {
        if (artist !== undefined) {
            const p: TimePeriod[] = artist.timeActive === undefined ? [] : [ ...artist.timeActive ];
            p.splice(index, 1);
            setArtist({ ...artist, timeActive: p });
        }
    }
    
    const handlePeriodChange = (index: number, period: TimePeriod) => {
        if (artist !== undefined) {
            const timeActive = artist.timeActive;
            if (timeActive !== undefined) {
                const p: TimePeriod[] = [
                    ...timeActive.slice(0, index),
                    period,
                    ...timeActive.slice(index + 1),
                ];

                setArtist({ ...artist, timeActive: p });
            }
        }
    }
    
    const handleAddPeriod = () => {
        if (artist !== undefined) {
            const timeActive: TimePeriod[] = artist.timeActive === undefined ? [] : [ ...artist.timeActive ];
            timeActive.push({ 
                origin: { date: newPeriodOriginDate, month: newPeriodOriginMonth, year: newPeriodOriginYear },
                end: { date: newPeriodEndDate, month: newPeriodEndMonth, year: newPeriodEndYear },
            });

            setArtist({ ...artist, timeActive });
            setNewPeriodOriginDate(-1);
            setNewPeriodOriginMonth(-1);
            setNewPeriodOriginYear(-1);
            setNewPeriodEndDate(-1);
            setNewPeriodEndMonth(-1);
            setNewPeriodEndYear(-1);
        }
    }

    const handleProfileChange = (profile: string) => {
        if (artist !== undefined) {
            const a: Artist = { ...artist, profile };
            setArtist(a);
        }
    }

    const handleLocationChange = (location: Location) => {
        if (artist !== undefined) {
            const a: Artist = {
                ...artist,
                location : location
            };

            setArtist(a);
        }
    }
    
    const handleFileChange  = (event: ChangeEvent<HTMLInputElement>) => { 
        if (event.target.files && event.target.files.length > 0) {
            setSelectedFile(event.target.files[0]);
        }
    }
    
    const fileToUint8Array = async (file: File): Promise<Uint8Array> => {
        const arrayBuffer = await file.arrayBuffer();
        return new Uint8Array(arrayBuffer);
    }
    
    const handleUpload = () => {
        if (selectedFile) {
            fileToUint8Array(selectedFile).then((f) => {
                const formData = new FormData();
                const byteArray = new Uint8Array(f);
                const blob = new Blob([byteArray]);
                formData.append('file', blob, 'logo.jpg');
                if (params.letter !== undefined && params.artistName !== undefined) {
                    postArtistLogo(getAccessToken(), params.artistName, formData);
                }
                
                setTimeout(() => callApi<FileResponse>(getArtistLogo(getAccessToken(), params.artistName ?? ''), setLogo), 2000);
            });

            setSelectedFile(null); // Reset the selected file after upload
        }
    };
        
    const handleDelete = async () => {
        if (artist !== undefined) {
            var deleted = await deleteArtistLogo(getAccessToken(), params.artistName ?? '');
            if (deleted === true) {
                setLogo(undefined);
            }
        }
    }
    
    return (
        <>
            <ButtonBar
                leftButtons={[
                    <Button component={Link} variant="contained" size="small" to={`/letters/${params.letter}/artists/${params.artistName}`}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faArrowLeft} className="icon"/>
                        Back To Artist                        
                    </Button>
                ]}
                rightButtons={[
                    <Button variant="contained" size="small" onClick={handleSave}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faFloppyDisk} className="icon"/>
                        Save Changes
                    </Button>
                ]}
            />
            <Stack direction={isSmall ? 'column' : 'row'} spacing={2} sx={{ justifyContent: 'center' }}>
                <Box sx={{ width: isSmall ? '100%' : '628px' }}>
                    <Box sx={{ mb: '20px' }}><Typography variant="h6">Artist Information</Typography></Box>
                    <Stack spacing={3}>
                        <Box>
                            <TextField 
                                id="artist-name"
                                sx={{ width: '420px' }}
                                size="small"
                                label="Name"
                                slotProps={{inputLabel: { shrink: true } }}
                                variant="outlined" 
                                value={artist?.name}
                                onChange={(e) => setArtist({ ...artist, name: e.target.value })}
                            />
                        </Box>
                        <Box>
                            <TextField
                                id="artist-profile"
                                sx={{ width: '100%' }}
                                maxRows={20}
                                size="small"
                                label={'Profile'}
                                slotProps={{inputLabel: { shrink: true } }}
                                variant="outlined"
                                multiline 
                                value={artist?.profile}
                                onChange={(e) => handleProfileChange(e.target.value)}
                            />
                        </Box>
                    </Stack>
                </Box>
                <Box>
                    <Box sx={{ mb: '20px' }}><Typography variant="h6">Page Styles</Typography></Box>
                    <Stack spacing={3}>
                        {artist?.styles?.map((style: Style, index: number) =>
                            <StyleRow index={index} style={style} handleDelete={handleStyleDelete} handleChange={handleStyleChange} />
                        )}
                        <Box>
                            <Stack direction="row" spacing={1}>
                                <Box>
                                    <TextField
                                        sx={{ width: isSmall ? '120px' : '180px' }}
                                        size="small"
                                        label={`Style`}
                                        variant="outlined"
                                        value={newStyleKey}
                                        onChange={(e) => setNewStyleKey(e.target.value)}
                                    />
                                </Box>
                                <Box>
                                    <TextField
                                        sx={{ width: isSmall ? '120px' : '240px' }}
                                        size="small"
                                        label={`Value`}
                                        variant="outlined"
                                        value={newStyleValue}
                                        onChange={(e) => setNewStyleValue(e.target.value)}
                                    />
                                </Box>
                                <Box>
                                    <Button sx={{ my: 0.5 }} variant="contained" color="primary" size="small" onClick={handleAddStyle}>Add</Button>
                                </Box>
                            </Stack>
                        </Box>
                    </Stack>
                    <Divider style={{ paddingTop: '18px', paddingBottom: '12px' }} />
                    <Box sx={{ mb: '20px' }}><Typography variant="h6">Location</Typography></Box>
                    <Stack spacing={3}>
                        <Box>
                            <Stack direction="row" spacing={1}>
                                <Box>
                                    <TextField
                                        sx={{ width: isSmall ? '160px' : '160px' }}
                                        size="small"
                                        label="Country"
                                        variant="outlined"
                                        value={artist?.location?.country}
                                        slotProps={{inputLabel: { shrink: true } }}
                                        onChange={(e) => handleLocationChange({ ...artist?.location, country: e.target.value })}
                                    />
                                </Box>
                                <Box>
                                    <TextField
                                        sx={{ width: isSmall ? '160px' : '160px' }}
                                        size="small"
                                        label="State/Providence"
                                        variant="outlined"
                                        value={artist?.location?.stateProvidence}
                                        slotProps={{inputLabel: { shrink: true } }}
                                        onChange={(e) => handleLocationChange({ ...artist?.location, stateProvidence: e.target.value })}
                                    />
                                </Box>
                                <Box>
                                    <TextField
                                        sx={{ width: isSmall ? '160px' : '160px' }}
                                        size="small"
                                        label="City"
                                        variant="outlined"
                                        value={artist?.location?.city}
                                        slotProps={{inputLabel: { shrink: true } }}
                                        onChange={(e) => handleLocationChange({ ...artist?.location, city: e.target.value })}
                                    />
                                </Box>
                            </Stack>
                        </Box>
                    </Stack>         
                    <Divider style={{ paddingTop: '18px', paddingBottom: '12px' }} />
                    <Box sx={{ mb: '20px' }}><Typography variant="h6">Time Periods</Typography></Box>
                    <Stack spacing={3}>
                        {artist?.timeActive?.map((period: TimePeriod, index: number) =>
                            <PeriodRow index={index} period={period} handleDelete={handlePeriodDelete} handleChange={handlePeriodChange} />
                        )}
                        <Box>
                            <Stack direction="row" spacing={1}>
                                <DateField value={newPeriodOriginDate} label="Date" handleChange={(e) => setNewPeriodOriginDate(e)} />
                                <DateField value={newPeriodOriginMonth} label="Month" handleChange={(e) => setNewPeriodOriginMonth(e)} />
                                <DateField value={newPeriodOriginYear} label="Year" handleChange={(e) => setNewPeriodOriginYear(e)} />
                                <Box>
                                    <Button sx={{ my: 0.5 }} variant="contained" color="primary" size="small" onClick={handleAddPeriod}>Add</Button>
                                </Box>
                            </Stack>
                        </Box>
                        <Box>
                            <Stack direction="row" spacing={1}>
                                <DateField value={newPeriodEndDate} label="Date" handleChange={(e) => setNewPeriodEndDate(e)} />
                                <DateField value={newPeriodEndMonth} label="Month" handleChange={(e) => setNewPeriodEndMonth(e)} />
                                <DateField value={newPeriodEndYear} label="Year" handleChange={(e) => setNewPeriodEndYear(e)} />
                                <Box></Box>
                            </Stack>
                        </Box>
                    </Stack>
                </Box>
            </Stack>
            <br /><br />
            <Stack spacing={2} sx={{ justifyContent: 'center' }}>
                <Box>
                    <Stack direction="row">
                        <Box>
                            <TextField type="file" variant="outlined" size="small" margin="none" onChange={handleFileChange} />
                        </Box>
                        <Box sx={{ paddingLeft: '18px' }}> 
                            <Button variant="contained" color="primary" onClick={handleUpload} disabled={!selectedFile}>
                                <FontAwesomeIcon fontSize="large" color="primary" icon={faCloudArrowUp} className="icon"/>
                                Upload Logo
                            </Button>
                        </Box> 
                    </Stack>
                </Box>
                <Box>
                    {logo &&
                        <>
                            <Box component="img" alt={'Album Cover'} src={`data:image/jpeg;base64,${logo?.fileContents}`} />
                            <BeatLoader
                                loading={logo?.fileContents === undefined}
                                size={24}
                                aria-label="Loading Spinner"
                                data-testid="loader"
                            />
                            <Box sx={{ paddingLeft: '18px' }}> 
                                <Button variant="contained" color="primary" onClick={handleDelete}>
                                    <FontAwesomeIcon fontSize="large" color="primary" icon={faTrashCan} className="icon"/>
                                    Delete Logo
                                </Button>
                            </Box>
                        </>
                    }
                </Box>
            </Stack>
        </>
    );
}

export default EditArtist;